/**
 * A data tile's display choices for its rows (`tile.categories`): order,
 * hiding and renaming, applied to the model the tile shows. Display only:
 * the tile's data and script are untouched, and row ids stay as the table
 * gives them.
 */

import type { TableModel } from "utils/tableModel"
import type {
  CategoryDisplay,
  DataTile,
} from "features/Dashboard/types/dashboardDocument"
import { DownRow, downRowsOf, withDownRows } from "./downRows"
import { leafLabel } from "./modelAdapters"

/** A row a tile can show: a category of its last result, or a measure. */
export interface DisplayRow {
  id: string
  label: string
  measure: boolean
}

/**
 * The rows `tile` can show, before its category display: the categories of
 * `model` (its last result; each once, Totals left out) unless it shows
 * only its measures, then its measures unless it shows only its
 * categories. A tile without measures shows its categories whatever it
 * asks for.
 */
export const displayRows = (
  tile: DataTile,
  model: TableModel | undefined
): DisplayRow[] => {
  const { measures } = tile
  const rows = tile.display.rows
  const seen = new Set<string>()
  const categories = (model?.down.leaves ?? []).flatMap((l) => {
    if (l.isTotal || seen.has(l.id)) return []
    seen.add(l.id)
    return [{ id: l.id, label: leafLabel(l), measure: false }]
  })
  return [
    ...(rows !== "measures" || measures.length === 0 ? categories : []),
    ...(rows !== "categories"
      ? measures.map((m) => ({ id: m.id, label: m.label || m.id, measure: true }))
      : []),
  ]
}

/**
 * `rows` as the view orders them: the ids in `order` first, by their
 * position there, the rest after them in list order; categories stay above
 * measures, as Totals keep them apart in the view.
 */
export const orderDisplayRows = (
  rows: DisplayRow[],
  order: string[] | undefined
): DisplayRow[] => {
  const rank = new Map<string, number>()
  order?.forEach((id, i) => {
    if (!rank.has(id)) rank.set(id, i)
  })
  const unlisted = order?.length ?? 0
  const sorted = (group: DisplayRow[]): DisplayRow[] =>
    group
      .map((row, i) => ({ row, i }))
      .sort(
        (a, b) =>
          (rank.get(a.row.id) ?? unlisted) - (rank.get(b.row.id) ?? unlisted) ||
          a.i - b.i
      )
      .map((x) => x.row)
  return [
    ...sorted(rows.filter((r) => !r.measure)),
    ...sorted(rows.filter((r) => r.measure)),
  ]
}

/**
 * `model` with its rows reordered, hidden and renamed by `display`: the ids
 * in `order` first, by their position there, the other rows after them in
 * table order; the ids in `hidden` dropped; the ids in `labels` shown under
 * the new label (the last segment of the header path, the id kept). A
 * category's statistic rows share its id, so they move, hide and rename
 * together. Totals rows stay where they are: categories are ordered above
 * them and measures below them. Pure; returns `model` itself when there is
 * nothing to apply.
 */
export const applyCategoryDisplay = (
  model: TableModel,
  display: CategoryDisplay
): TableModel => {
  const { order, hidden, labels } = display
  if (
    !order?.length &&
    hidden.length === 0 &&
    Object.keys(labels).length === 0
  ) {
    return model
  }
  const hiddenIds = new Set(hidden)
  const rank = new Map<string, number>()
  order?.forEach((id, i) => {
    if (!rank.has(id)) rank.set(id, i)
  })
  const unlisted = order?.length ?? 0
  const rankOf = (row: DownRow): number => rank.get(row.leaf.id) ?? unlisted
  const byRank = (a: DownRow, b: DownRow): number =>
    rankOf(a) - rankOf(b) || (a.from ?? 0) - (b.from ?? 0)

  const rows: DownRow[] = []
  let run: DownRow[] = []
  const flush = (): void => {
    rows.push(...run.sort(byRank))
    run = []
  }
  downRowsOf(model).forEach((row) => {
    if (hiddenIds.has(row.leaf.id)) return
    if (row.leaf.isTotal) {
      flush()
      rows.push(row)
    } else {
      run.push(row)
    }
  })
  flush()

  const renamed = (row: DownRow): DownRow => {
    const label = Object.hasOwn(labels, row.leaf.id)
      ? labels[row.leaf.id]
      : ""
    if (!label || row.leaf.path.length === 0) return row
    const path = [...row.leaf.path]
    path[path.length - 1] = label
    return { ...row, leaf: { ...row.leaf, path } }
  }
  return withDownRows(model, rows.map(renamed))
}
